// Order-list trust surfaces — extracted from index.tsx (#340).
//
// Both were written after the Jun 11 incident: the open-orders refresh loop
// failed silently for minutes while a trader re-placed "missing" (actually
// resting) orders. The badge makes staleness visible; the banner shouts the
// specific shape where the relay's intent tracker says contracts are resting
// but the list below is empty.
//
// They are children rather than inline JSX for a reason that survives the
// move: each re-renders on a display cadence (5s / 2.5s via the shared
// display clock, #333), so keeping them separate stops that tick re-rendering
// the 7k-line view.
//
// Nothing here reads a store, fetches, or mutates an order — retry and refresh
// cross the boundary as callbacks. Every threshold below is tuned and pinned by
// ./ordersSyncStatus.test.tsx.
import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import type { OrdersSyncState } from '../../stores/accountStore';
import { useSharedNow } from '../../lib/infra/sharedClock';

/** How recent the last successful open-orders read must be for the list to be
 *  trusted as current. */
export const ORDERS_SYNC_FRESH_MS = 20_000;

/** True when the open-orders list can be trusted as current: the loop has not
 *  failed since its last success, and that success is under
 *  ORDERS_SYNC_FRESH_MS old. `openOrders` keeps the last good snapshot while
 *  the loop fails, so anything that ACTS on the list (not just displays it)
 *  must gate on this. One predicate for the mismatch banner and the close
 *  modal's close-and-cancel (#949) so the threshold can't drift. */
export function isOrdersSyncFresh(sync: OrdersSyncState, nowMs: number): boolean {
  return (
    sync.consecutiveFailures === 0 &&
    sync.lastSuccessMs !== null &&
    nowMs - sync.lastSuccessMs < ORDERS_SYNC_FRESH_MS
  );
}

/** Amber pill when the open-orders refresh loop is stale or failing.
 *  Child component so its 5s aging tick doesn't re-render the whole page.
 *  Jun 11 incident: the loop failed silently for minutes while a trader
 *  re-placed "missing" (actually resting) orders — staleness must be
 *  visible the moment the list can no longer be trusted. */
export function formatSyncAge(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 120) return `${s}s`;
  return `${Math.round(s / 60)}m`;
}

export function OrdersSyncBadge({
  sync,
  active,
  onRetry,
}: {
  sync: OrdersSyncState;
  /** Any non-disconnected session state — the badge must stay visible
   *  through 'connecting'/'error' wedges (that's exactly when the list
   *  can't be trusted), hiding only on a deliberate disconnect. */
  active: boolean;
  onRetry: () => void;
}) {
  'use memo'; // #347 React Compiler cluster — docs/react-compiler-clusters/position-tracker-sync-status-347.md
  const [mountMs] = useState(() => Date.now());
  const [throttledUntil, setThrottledUntil] = useState(0);
  const sharedNowMs = useSharedNow(5_000, active);
  // Shared-clock registries outlive their last subscriber, so the first
  // snapshot of a later mount can predate this mount until subscription
  // re-stamps it. Taking the fresh mount timestamp prevents a negative or
  // under-counted age without touching the shared-clock implementation.
  const nowMs = Math.max(mountMs, sharedNowMs);

  useEffect(() => {
    if (throttledUntil === 0) return;
    const delayMs = Math.max(0, throttledUntil - Date.now());
    const id = window.setTimeout(() => setThrottledUntil(0), delayMs);
    return () => window.clearTimeout(id);
  }, [throttledUntil]);

  if (!active) return null;
  const baseline = sync.lastSuccessMs ?? mountMs;
  const ageMs = nowMs - baseline;
  // >=2 consecutive failures: a single blip at the 5s poll cadence heals on
  // the next tick — surfacing it would train traders to ignore the badge.
  const failing = sync.consecutiveFailures >= 2;
  if (!failing && ageMs < 30_000) return null;
  const throttled = throttledUntil !== 0;
  const label = failing
    ? `Orders sync failing (${sync.consecutiveFailures}×) — retry`
    : sync.lastSuccessMs === null
      ? 'Orders not synced yet — retry'
      : `Orders synced ${formatSyncAge(ageMs)} ago — retry`;
  return (
    <button
      onClick={() => {
        if (throttled) return;
        setThrottledUntil(Date.now() + 3_000);
        onRetry();
      }}
      disabled={throttled}
      className="border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
      title={
        failing
          ? `Last error: ${sync.lastError ?? 'unknown'} — click to retry now`
          : 'The order list has not refreshed recently — click to retry now'
      }
    >
      <RefreshCw size={11} />
      {label}
    </button>
  );
}

interface OrdersMismatchBannerProps {
  intentContracts: number;
  /** UNFILTERED unified order count (kalshi + poly) — never the
   *  sport-filtered list, or an include/exclude chip that hides every
   *  order fires a false "out of sync" alarm in a healthy session and
   *  teaches traders to ignore the one banner that must be trusted. */
  listedCount: number;
  /** consecutiveFailures from ordersSync — keeps the banner alive past the
   *  5-min intent TTL when the refresh loop itself is persistently down. */
  syncFailures: number;
  /** True when the orders fetch loop is demonstrably healthy (fresh
   *  success, zero failure streak). Flips the copy from "list is likely
   *  out of sync" to the venue-truth message: during the Jun 12 mass-
   *  cancel sweep the list was CORRECT (orders really were canceled at
   *  Kalshi) and the out-of-sync wording sent traders chasing a refresh
   *  bug that wasn't there (grim's screenshot). */
  syncHealthy: boolean;
  active: boolean;
  onRefresh: () => void;
}

type ActiveOrdersMismatchBannerProps = Pick<
  OrdersMismatchBannerProps,
  'intentContracts' | 'syncHealthy' | 'onRefresh'
>;

/** Loud banner for the exact Jun 11 failure shape: the relay's intent
 *  tracker says contracts are resting (orders accepted at the venue) but
 *  the list below is EMPTY. Without this, the natural trader response is
 *  to re-place — stacking live exposure on invisible orders.
 *
 *  This is the public qualification gate; the 10s hysteresis lives in the
 *  mount-scoped active leaf below, so clearing the mismatch unmounts the
 *  leaf and a returning mismatch starts a fresh hold with no ref or
 *  state-writing effect. */
export function OrdersMismatchBanner({
  intentContracts,
  listedCount,
  syncFailures,
  syncHealthy,
  active,
  onRefresh,
}: OrdersMismatchBannerProps) {
  const mismatch =
    active && listedCount === 0 && (intentContracts > 0 || syncFailures >= 4);
  if (!mismatch) return null;
  return (
    <ActiveOrdersMismatchBanner
      intentContracts={intentContracts}
      syncHealthy={syncHealthy}
      onRefresh={onRefresh}
    />
  );
}

/** Hysteresis: only shows after the mismatch persists 10s, so the normal
 *  place→list-catches-up gap never flashes it. Exported so the compiler
 *  evidence harness can resolve the annotated render function; production
 *  composition goes through OrdersMismatchBanner only. */
export function ActiveOrdersMismatchBanner({
  intentContracts,
  syncHealthy,
  onRefresh,
}: ActiveOrdersMismatchBannerProps) {
  'use memo'; // #347 React Compiler cluster — docs/react-compiler-clusters/position-tracker-sync-status-347.md
  const [firstSeenMs] = useState(() => Date.now());
  const sharedNowMs = useSharedNow(2_500);
  // Same guard as the badge: a dormant 2.5s registry's last snapshot must
  // not seed a rearming mismatch with a stale baseline.
  const nowMs = Math.max(firstSeenMs, sharedNowMs);
  if (nowMs - firstSeenMs < 10_000) return null;

  return (
    <div className="border-amber-500/50 bg-amber-500/10 flex items-center justify-between gap-3 rounded-lg border px-4 py-3">
      <div className="text-amber-300 light:text-amber-700 text-sm">
        <span className="font-semibold">
          {intentContracts > 0
            ? `Your account shows ${intentContracts.toLocaleString('en-US')} contracts in recent resting buys, but no orders are displayed.`
            : 'The order list has failed to refresh repeatedly and may be missing your orders.'}
        </span>{' '}
        {syncHealthy
          ? 'Kalshi reports no resting orders for this account — recent buys were likely canceled at the venue or already filled. Check Fills before re-placing.'
          : "The list is likely out of sync — refresh before assuming an order didn't go through, and don't re-place orders you've already submitted."}
      </div>
      <button
        onClick={onRefresh}
        className="border-amber-500/60 bg-amber-500/20 text-amber-200 light:text-amber-800 hover:bg-amber-500/30 shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors"
      >
        Refresh orders
      </button>
    </div>
  );
}
